import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Receipt } from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { errorText } from '../../../lib/errors';
import {
  Card, CardHeader, CardBody, Field, Input, Select, Textarea, Button, DataTable, Badge, StatusBadge, Modal, ConfirmDialog, EmptyState, Switch,
} from '../../../ui';
import { changedFields } from './ProfileTab';

const PLANS = ['trial', 'starter', 'pro', 'enterprise'];
const CYCLES = ['monthly', 'quarterly', 'yearly'];
const CURRENCIES = ['UAH', 'USD', 'EUR'];
const METHODS = ['bank_transfer', 'card', 'cash', 'invoice'];
const PAYMENT_STATUSES = ['completed', 'pending', 'refunded'];
const PAYMENT_TONE = { completed: 'success', pending: 'warning', refunded: 'neutral' };
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, yearly: 12 };

const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');
const PLAN_FIELDS = ['plan', 'plan_price', 'currency', 'billing_cycle', 'billing_next_date', 'contract_number', 'contract_date', 'billing_email'];

export default function BillingTab({ tenant, onSaved }) {
  const { t, fmt } = useI18n();
  const initial = useMemo(() => Object.fromEntries(PLAN_FIELDS.map((f) => [
    f, f.endsWith('_date') ? dateOnly(tenant[f]) : (tenant[f] ?? ''),
  ])), [tenant]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial); }, [initial]);

  const loadPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const res = await api.get(`/admin/tenants/${tenant.id}/payments`);
      setPayments(res.data.data || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoadingPayments(false);
    }
  }, [tenant.id, t]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const changes = changedFields(form, initial);
  const dirty = Object.keys(changes).length > 0;
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const savePlan = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...changes };
      if ('plan_price' in payload) payload.plan_price = Number(payload.plan_price) || 0;
      if ('billing_email' in payload) payload.billing_email = payload.billing_email.trim();
      await api.put(`/admin/tenants/${tenant.id}`, payload);
      toast.success(t('common.saved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const toggleReminders = async (enabled) => {
    try {
      await api.put(`/admin/tenants/${tenant.id}`, { billing_reminders: enabled });
      toast.success(t('common.saved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const removePayment = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${tenant.id}/payments/${deleting.id}`);
      toast.success(t('billing.paymentDeleted'));
      setDeleting(null);
      loadPayments();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const monthly = (Number(form.plan_price) || 0) / (CYCLE_MONTHS[form.billing_cycle] || 1);

  const columns = [
    { key: 'payment_date', header: t('billing.paymentDate'), sortable: true, render: (p) => <span className="tabular">{fmt.date(p.payment_date)}</span> },
    { key: 'amount', header: t('billing.amount'), sortable: true, align: 'right', render: (p) => <span className="tabular font-medium text-zinc-900 dark:text-zinc-100">{fmt.money(p.amount, p.currency)}</span> },
    {
      key: 'period',
      header: t('billing.period'),
      hideOnMobile: true,
      render: (p) => (p.period_start || p.period_end
        ? <span className="tabular text-zinc-500">{fmt.date(p.period_start)} – {fmt.date(p.period_end)}</span>
        : '—'),
    },
    { key: 'payment_method', header: t('billing.method'), hideOnMobile: true, render: (p) => t(`billing.methods.${p.payment_method}`) },
    { key: 'invoice_number', header: t('billing.invoice'), hideOnMobile: true, render: (p) => p.invoice_number || '—' },
    { key: 'status', header: t('billing.paymentStatus'), render: (p) => <Badge tone={PAYMENT_TONE[p.status] ?? 'neutral'}>{t(`billing.paymentStatuses.${p.status}`)}</Badge> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'right',
      render: (p) => (
        <Button variant="danger-ghost" size="icon-sm" onClick={() => setDeleting(p)} aria-label={t('common.delete')}>
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={savePlan}>
        <Card>
          <CardHeader
            title={t('billing.planSection')}
            actions={<StatusBadge kind="billing" value={tenant.billing_next_date && tenant.plan_price > 0
              ? (new Date(tenant.billing_next_date) < new Date() ? 'overdue' : (new Date(tenant.billing_next_date) - new Date()) / 864e5 <= 7 ? 'due_soon' : 'ok')
              : 'none'} />}
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label={t('companies.plan')}>
                <Select value={form.plan} onChange={set('plan')}>
                  {PLANS.map((p) => <option key={p} value={p}>{t(`plans.${p}`)}</option>)}
                </Select>
              </Field>
              <Field label={t('companies.price')} hint={monthly > 0 ? `${fmt.money(monthly, form.currency)}${t('perMonth')}` : null}>
                <Input type="number" min="0" step="1" value={form.plan_price} onChange={set('plan_price')} className="tabular" />
              </Field>
              <Field label={t('companies.cycle')}>
                <Select value={form.billing_cycle} onChange={set('billing_cycle')}>
                  {CYCLES.map((c) => <option key={c} value={c}>{t(`cycles.${c}`)}</option>)}
                </Select>
              </Field>
              <Field label={t('companies.currency')}>
                <Select value={form.currency} onChange={set('currency')}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label={t('billing.nextDate')}>
                <Input type="date" value={form.billing_next_date} onChange={set('billing_next_date')} />
              </Field>
              <Field label={t('billing.contractNumber')}>
                <Input value={form.contract_number} onChange={set('contract_number')} maxLength={100} />
              </Field>
              <Field label={t('billing.contractDate')}>
                <Input type="date" value={form.contract_date} onChange={set('contract_date')} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('reminders.billingEmail')} hint={t('reminders.billingEmailHint', { email: tenant.contact_email || '—' })}>
                <Input type="email" value={form.billing_email} onChange={set('billing_email')} maxLength={200} placeholder={tenant.contact_email || ''} />
              </Field>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
                <div>
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('reminders.emailsOn')}</p>
                  <p className="text-xs text-zinc-500">{t('reminders.emailsOnHint')}</p>
                </div>
                <Switch checked={tenant.billing_reminders !== false} onChange={toggleReminders} label={t('reminders.emailsOn')} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" disabled={!dirty || saving} onClick={() => setForm(initial)}>{t('common.cancel')}</Button>
              <Button type="submit" icon={Save} loading={saving} disabled={!dirty}>{t('common.save')}</Button>
            </div>
          </CardBody>
        </Card>
      </form>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('billing.paymentsTitle')}</h2>
          <Button size="sm" icon={Plus} onClick={() => setAddOpen(true)}>{t('billing.addPayment')}</Button>
        </div>
        <DataTable
          columns={columns}
          rows={payments}
          loading={loadingPayments}
          empty={<EmptyState icon={Receipt} title={t('billing.noPayments')} />}
        />
      </div>

      <AddPaymentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        tenant={tenant}
        onDone={() => { loadPayments(); onSaved(); }}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={removePayment}
        loading={busy}
        title={t('billing.deletePaymentTitle')}
        text={t('billing.deletePaymentText')}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}

function addMonths(isoDate, months) {
  const d = new Date(isoDate);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function AddPaymentModal({ open, onClose, tenant, onDone }) {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const blank = () => {
    const start = dateOnly(tenant.billing_next_date) || today;
    return {
      amount: tenant.plan_price || '',
      currency: tenant.currency || 'UAH',
      payment_date: today,
      period_start: start,
      period_end: addMonths(start, CYCLE_MONTHS[tenant.billing_cycle] || 1),
      payment_method: 'bank_transfer',
      invoice_number: '',
      status: 'completed',
      notes: '',
    };
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(blank()); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      payload.amount = Number(form.amount);
      await api.post(`/admin/tenants/${tenant.id}/payments`, payload);
      toast.success(t('billing.paymentAdded'));
      onClose();
      onDone();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('billing.addPayment')}
      description={tenant.name}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="add-payment-form" loading={saving}>{t('common.add')}</Button>
        </>
      }
    >
      <form id="add-payment-form" onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('billing.amount')} required>
            <Input type="number" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} required className="tabular" />
          </Field>
          <Field label={t('companies.currency')}>
            <Select value={form.currency} onChange={set('currency')}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
          <Field label={t('billing.paymentDate')}>
            <Input type="date" value={form.payment_date} onChange={set('payment_date')} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('billing.periodStart')}>
            <Input type="date" value={form.period_start} onChange={set('period_start')} />
          </Field>
          <Field label={t('billing.periodEnd')} hint={t('billing.periodEndHint')}>
            <Input type="date" value={form.period_end} onChange={set('period_end')} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('billing.method')}>
            <Select value={form.payment_method} onChange={set('payment_method')}>
              {METHODS.map((m) => <option key={m} value={m}>{t(`billing.methods.${m}`)}</option>)}
            </Select>
          </Field>
          <Field label={t('billing.invoice')}>
            <Input value={form.invoice_number} onChange={set('invoice_number')} maxLength={100} />
          </Field>
          <Field label={t('billing.paymentStatus')}>
            <Select value={form.status} onChange={set('status')}>
              {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{t(`billing.paymentStatuses.${s}`)}</option>)}
            </Select>
          </Field>
        </div>
        <Field label={t('billing.notes')}>
          <Textarea value={form.notes} onChange={set('notes')} rows={2} maxLength={500} />
        </Field>
      </form>
    </Modal>
  );
}
